/**
 * Custom hook for PDF generation
 * Provides easy-to-use functions for generating PDFs throughout the application
 */

import { useState, useCallback } from 'react';
import {
  generatePDFFromElement,
  generateContractPDF,
  generateAmortizationSchedulePDF,
  generateReportPDF,
  previewPDF,
  getPDFDataURL,
} from '../utils/documentGenerator';
import { useTenant } from '../contexts/TenantContext';

export const usePDFGenerator = () => {
  const { tenant } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Generate PDF from an HTML element
   */
  const generateFromElement = useCallback(async (elementId, filename, options = {}) => {
    setIsGenerating(true);
    setError(null);

    try {
      const result = await generatePDFFromElement(elementId, filename, options);
      return result;
    } catch (err) {
      console.error('Error generating PDF from element:', err);
      setError(err.message || 'Failed to generate PDF');
      throw err;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  /**
   * Generate contract PDF
   */
  const generateContract = useCallback(async (contractData, filename, options = {}) => {
    setIsGenerating(true);
    setError(null);

    try {
      const pdf = await generateContractPDF(contractData, filename, { roundingPrecision: dp, ...options });
      return pdf;
    } catch (err) {
      console.error('Error generating contract PDF:', err);
      setError(err.message || 'Failed to generate contract PDF');
      throw err;
    } finally {
      setIsGenerating(false);
    }
  }, [dp]);

  /**
   * Generate amortization schedule PDF
   */
  const generateAmortizationSchedule = useCallback(async (scheduleData, filename, options = {}) => {
    setIsGenerating(true);
    setError(null);

    try {
      const pdf = await generateAmortizationSchedulePDF(scheduleData, filename, { roundingPrecision: dp, ...options });
      return pdf;
    } catch (err) {
      console.error('Error generating amortization schedule PDF:', err);
      setError(err.message || 'Failed to generate amortization schedule PDF');
      throw err;
    } finally {
      setIsGenerating(false);
    }
  }, [dp]);

  /**
   * Generate report PDF
   */
  const generateReport = useCallback(async (reportData, filename, options = {}) => {
    setIsGenerating(true);
    setError(null);

    try {
      const pdf = await generateReportPDF(reportData, filename, options);
      return pdf;
    } catch (err) {
      console.error('Error generating report PDF:', err);
      setError(err.message || 'Failed to generate report PDF');
      throw err;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  /**
   * Preview PDF in new window
   */
  const preview = useCallback((pdf) => {
    previewPDF(pdf);
  }, []);

  /**
   * Get PDF as data URL
   */
  const getDataURL = useCallback((pdf) => {
    return getPDFDataURL(pdf);
  }, []);

  /**
   * Clear any errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isGenerating,
    error,
    generateFromElement,
    generateContract,
    generateAmortizationSchedule,
    generateReport,
    preview,
    getDataURL,
    clearError,
  };
};

export default usePDFGenerator;
